import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { IEditContactDialogProps } from '@/@types/serviceTypes';
import type { Id } from '@convex/_generated/dataModel';

export function EditContactDialog({
  open,
  onOpenChange,
  leadId,
  phoneNumber,
  email,
  whatsappPhoneNumber,
  telegramUsername,
}: IEditContactDialogProps) {
  const updateContactInfo = useMutation(api.leads.updateContactInfo);
  const [saving, setSaving] = useState(false);
  const [formPhone, setFormPhone] = useState(phoneNumber || '');
  const [formEmail, setFormEmail] = useState(email || '');
  const [formWhatsapp, setFormWhatsapp] = useState(whatsappPhoneNumber || '');
  const [formTelegram, setFormTelegram] = useState(telegramUsername || '');

  const resetForm = useCallback(() => {
    setFormPhone(phoneNumber || '');
    setFormEmail(email || '');
    setFormWhatsapp(whatsappPhoneNumber || '');
    setFormTelegram(telegramUsername || '');
  }, [phoneNumber, email, whatsappPhoneNumber, telegramUsername]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetForm();
      onOpenChange(next);
    },
    [onOpenChange, resetForm]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const typedLeadId = leadId as Id<'leads'>; // AS_JUSTIFIED: leadId comes from props as string; Convex guarantees valid Id<"leads">
      await updateContactInfo({
        leadId: typedLeadId,
        ...(formPhone !== (phoneNumber || '') ? { phoneNumber: formPhone } : {}),
        ...(formEmail !== (email || '') ? { email: formEmail } : {}),
        ...(formWhatsapp !== (whatsappPhoneNumber || '')
          ? { whatsappPhoneNumber: formWhatsapp }
          : {}),
        ...(formTelegram !== (telegramUsername || '') ? { telegramUsername: formTelegram } : {}),
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }, [
    formPhone,
    formEmail,
    formWhatsapp,
    formTelegram,
    leadId,
    phoneNumber,
    email,
    whatsappPhoneNumber,
    telegramUsername,
    updateContactInfo,
    onOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Contact Information</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-phone">Phone Number</Label>
            <Input
              id="edit-phone"
              value={formPhone}
              onChange={e => setFormPhone(e.target.value)}
              placeholder="+1 234 567 890"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-whatsapp">WhatsApp Number</Label>
            <Input
              id="edit-whatsapp"
              value={formWhatsapp}
              onChange={e => setFormWhatsapp(e.target.value)}
              placeholder="Same as phone if empty"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-telegram">Telegram Username</Label>
            <Input
              id="edit-telegram"
              value={formTelegram}
              onChange={e => setFormTelegram(e.target.value)}
              placeholder="@username"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={formEmail}
              onChange={e => setFormEmail(e.target.value)}
              placeholder="email@example.com"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
